/**
 * EVO — yetkazish va haydovchiga biriktirish ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/sales/deliveries.service.ts` va
 * `driver.service.ts` dagi javob interfeyslaridan MAYDONMA-MAYDON ko'chirilgan
 * (`lib/replenishment.ts` bilan bir xil naqsh). Bu qo'lda saqlanadigan
 * shartnoma: server ustunni qayta nomlasa TypeScript uni ushlamaydi.
 *
 * ============================================================================
 * NIMA UCHUN ALOHIDA FAYL, `lib/sales.ts` NING ICHIDA EMAS
 * ============================================================================
 * `lib/sales.ts` da yuk xati ALLAQACHON bor, lekin u BOSHQA savolga javob
 * beradi: «shu buyurtma bo'yicha qanday yuk xatlari chiqarilgan» (buyurtma
 * kartochkasidagi jadval). Bu yerdagi ekran esa teskari tomondan qaraydi:
 * «bugun nima yetkaziladi va kim olib ketadi» — unga haydovchi, biriktirish
 * vaqti, maydon fakti va naqd pul kerak.
 *
 * Umumiy qismi TAKRORLANMAYDI: `DeliveryAssignRow` `sales.ts` dagi
 * `DeliveryListRow` dan MEROS oladi. Ikkinchi nusxa yozilsa, ikkalasi bir kun
 * ajralib ketardi va qaysi biri to'g'ri ekani ko'rinmasdi.
 *
 * ============================================================================
 * IXTIYORIY MAYDON = RUXSAT YO'Q
 * ============================================================================
 * `cashCollectedAmount?` / `cashCollectedCurrency?` — server `cashaccount.view`
 * bo'lmasa bu ustunlarni javobga UMUMAN qo'shmaydi (`fulfilmentProjection()`),
 * `null` ham qilmaydi. JONLI TEKSHIRILGAN (`ombor@evo.uz`):
 *
 *   {"id":16,...,"failedAt":null,"failReason":null}          ← kalitlar YO'Q
 *
 * `admin@evo.uz` da esa aynan o'sha satr `"cashCollectedAmount":null` bilan
 * keladi. Ya'ni `?` bu yerda bezak emas: ekran ustunni RUXSATGA qarab quradi,
 * qiymatga qarab emas — aks holda omborchida har satrda «—» turgan bo'sh
 * ustun chiqardi va u «pul yig'ilmagan» degan YOLG'ON ma'no berardi.
 */
import { api } from './api';
import type { Page } from './products';
import type { DeliveryListRow } from './sales';

/** `deliveries.dto.ts` → `DeliveryListQuerySchema.status`. */
export const DELIVERY_STATUSES = ['draft', 'posted', 'cancelled'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/**
 * MAYDON holati — hujjat holati (`status`) bilan ARALASHTIRILMAYDI.
 *
 * `status = posted` degani tovar rafdan chiqdi; mijoz uni OLDI degani emas.
 * Shuning uchun serverda ham bu ikkisi alohida maydonlarda turadi
 * (`deliveries.service.ts` → `DeliveryFulfilmentFields` izohi).
 *
 * `unassigned` — bu yerda QO'SHILGAN to'rtinchi qiymat. Serverdagi
 * `DriverRouteStop.driverStatus` da u YO'Q va bo'lishi ham shart emas:
 * haydovchining o'z ekranida biriktirilmagan yetkazish umuman ko'rinmaydi.
 * Biriktirish ekranida esa aynan shu holat ASOSIY ish holati.
 */
export type DriverStatus = 'unassigned' | 'pending' | 'delivered' | 'failed';

/**
 * Ro'yxat satri — `DeliveriesService.list()` proyeksiyasi.
 *
 * Sana-vaqt maydonlari `string`: server `Date` qaytaradi, JSON esa uni ISO
 * satrga aylantiradi. `Date` deb yozish yolg'on bo'lardi — `formatDateTime`
 * ikkalasini ham qabul qiladi, lekin tip HAQIQATNI aytishi kerak.
 */
export interface DeliveryAssignRow extends DeliveryListRow {
  readonly driverUserId: number | null;
  readonly driverFullName: string | null;
  readonly assignedAt: string | null;
  readonly assignedBy: number | null;
  readonly deliveredAt: string | null;
  readonly receivedByName: string | null;
  readonly deliveredLatitude: string | null;
  readonly deliveredLongitude: string | null;
  readonly failedAt: string | null;
  readonly failReason: string | null;
  /** FAQAT `cashaccount.view` bilan keladi — fayl boshidagi izohga qarang. */
  readonly cashCollectedAmount?: string | null;
  readonly cashCollectedCurrency?: string | null;
}

/**
 * Maydon holatini satrdan CHIQARAMIZ.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN SERVERDAN OLINMAYDI                                           │
 * │                                                                          │
 * │ `driverStatus` javobda YO'Q: u faqat `GET /deliveries/my-route`          │
 * │ javobida bor (`DriverRouteStop`), ro'yxatda esa yo'q — jonli             │
 * │ tekshirilgan. Ro'yxat esa `deliveredAt` va `failedAt` ni to'liq beradi,  │
 * │ ya'ni holat ANIQ hosila: taxmin yo'q, yashirin qoida yo'q.               │
 * │                                                                          │
 * │ Tartib serverdagi bilan AYNAN bir xil (`driver.service.ts`:              │
 * │ `deliveredAt` avval, keyin `failedAt`). Teskari yozilsa, ikkala maydon   │
 * │ ham to'lgan (nazariy) satrda ikki ekran ikki xil javob berardi.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function driverStatusOf(row: DeliveryAssignRow): DriverStatus {
  if (row.deliveredAt !== null) return 'delivered';
  if (row.failedAt !== null) return 'failed';
  return row.driverUserId === null ? 'unassigned' : 'pending';
}

/**
 * Biriktirish MUMKINMI — `driver.service.ts` → `lockForAssign()` nusxasi.
 *
 * Server baribir 409 qaytaradi, ya'ni bu HIMOYA EMAS. Vazifasi boshqacha:
 * bosilmaydigan tugmani ko'rsatmaslik. «Biriktirish» tugmasini bosib
 * «yakunlangan yetkazishning haydovchisi o'zgartirilmaydi» degan javob olish —
 * xodimni ishonchsizlikka o'rgatadi.
 *
 * Shart AYNAN serverdagidek: holat `draft`/`posted` VA yetkazish hali
 * yakunlanmagan. Yumshoqroq yozilsa tugma chiqadi va 409 beradi.
 */
export function isAssignable(row: DeliveryAssignRow): boolean {
  if (row.status !== 'draft' && row.status !== 'posted') return false;
  return row.deliveredAt === null && row.failedAt === null;
}

/** `GET /api/deliveries/drivers` — faqat `id` va `fullName` (boshqasi so'ralmaydi). */
export interface DriverOption {
  readonly id: number;
  readonly fullName: string;
}

export interface DeliveryListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: 'asc' | 'desc';
  readonly q?: string | undefined;
  readonly status?: DeliveryStatus | undefined;
  readonly driverUserId?: number | undefined;
  readonly date?: string | undefined;
  /**
   * `true` bo'lgandagina yuboriladi.
   *
   * `unassigned=false` serverda HECH QANDAY filtr qo'ymaydi (DTO izohi), ya'ni
   * uni yuborish bekorga so'rov satrini uzaytirardi va URL da «nimadir
   * sozlangan» degan noto'g'ri taassurot berardi.
   */
  readonly unassigned?: true | undefined;
}

export interface AssignDriverBody {
  readonly driverUserId: number;
  readonly vehicleNo?: string;
}

/**
 * Biriktirish javobi — TO'LIQ hujjat qaytadi (`DeliveryResponse`), lekin
 * ekranga undan faqat hujjat raqami kerak: tasdiq xabarida ko'rsatiladi.
 *
 * Tor tip ataylab: ro'yxat baribir qayta so'raladi (`invalidateQueries`) va
 * javobdagi satrni jadvalga qo'lda yamash ikkinchi haqiqat manbai bo'lardi —
 * server ko'lam va maskalashni o'zi qo'llaydi, biz esa uni takrorlardik.
 */
export interface AssignResult {
  readonly id: number;
  readonly docNo: string;
}

/** `GET /api/deliveries/cash-to-collect` — `cashaccount.view`. */
export interface CashToCollectRow {
  readonly driverUserId: number;
  readonly driverFullName: string;
  readonly currency: string;
  readonly amount: string;
  readonly deliveryCount: number;
}

export interface CashTotal {
  readonly currency: string;
  readonly amount: string;
}

export interface CashToCollectResponse {
  readonly rows: readonly CashToCollectRow[];
  readonly totals: readonly CashTotal[];
}

export const deliveryKeys = {
  all: ['deliveries'] as const,
  list: (params: DeliveryListParams) => ['deliveries', 'list', params] as const,
  drivers: () => ['deliveries', 'drivers'] as const,
  cash: (date: string | undefined) => ['deliveries', 'cash', date ?? 'all'] as const,
};

export function fetchDeliveryList(
  params: DeliveryListParams,
  signal?: AbortSignal,
): Promise<Page<DeliveryAssignRow>> {
  return api.get<Page<DeliveryAssignRow>>('/deliveries', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      status: params.status,
      driverUserId: params.driverUserId,
      date: params.date,
      unassigned: params.unassigned,
    },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Faol dastavkachilar.
 *
 * Bu ALOHIDA endpoint va u `delivery.assign` bilan ochiladi — `user.view`
 * bilan EMAS. Sabab ekran uchun hal qiluvchi: biriktirishni OMBORCHI qiladi,
 * `GET /api/admin/users` esa unga 403 beradi (jonli tekshirilgan: `ombor`,
 * `moliya`, `sotuvchi` — uchalasiga ham 403). Ya'ni xodimlar ma'lumotnomasidan
 * ro'yxat qurish yo'li ekranni omborchida BUTUNLAY ishlamas qilardi.
 */
export function fetchDrivers(signal?: AbortSignal): Promise<readonly DriverOption[]> {
  return api.get<readonly DriverOption[]>('/deliveries/drivers', {
    ...(signal ? { signal } : {}),
  });
}

/**
 * `PUT` — amal IDEMPOTENT (controller izohi): bir xil haydovchini ikki marta
 * biriktirish natijani o'zgartirmaydi. Shuning uchun ekranda ikki marta
 * bosilgan tugma yangi hujjat yaratmaydi.
 */
export function assignDriver(id: number, body: AssignDriverBody): Promise<AssignResult> {
  return api.put<AssignResult>(`/deliveries/${id}/assign`, body);
}

export function unassignDriver(id: number): Promise<AssignResult> {
  return api.put<AssignResult>(`/deliveries/${id}/unassign`);
}

export function fetchCashToCollect(
  date: string | undefined,
  signal?: AbortSignal,
): Promise<CashToCollectResponse> {
  return api.get<CashToCollectResponse>('/deliveries/cash-to-collect', {
    query: { date },
    ...(signal ? { signal } : {}),
  });
}
